import { LoadingButton } from '@mui/lab'
import { Box, FormControl, FormControlLabel, FormGroup, IconButton, Switch, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AnimalDto, AnimalShareAnnounceDto } from '../../../interactors/gen/backendClient'
import { useEditAnimalMutation } from '../../../store/useEditAnimalMutation'
import { theme } from '../../theme'
import { OpenInNew, Save } from '@mui/icons-material'

interface ShareAnimalFormProps {
  animal: AnimalDto
}

export const ShareAnimalForm: React.FC<ShareAnimalFormProps> = ({ animal }) => {
  const { control, handleSubmit } = useForm<AnimalShareAnnounceDto>({
    defaultValues: {
      petso: animal.shareAnnounce?.petso ?? true,
      wamiz: animal.shareAnnounce?.wamiz ?? true,
    },
  })

  const editAnimalMutation = useEditAnimalMutation({})

  const isAdoptable =
    animal.adoption.status === 'adoptable' || animal.adoption.status === 'in-the-process-of-being-adoptable'

  const disabledTooltip = isAdoptable
    ? ''
    : "L'animal doit être en statut « adoptable » ou « en cours d'adoption » pour diffuser son annonce."

  const onSubmit = (data: AnimalShareAnnounceDto) => {
    editAnimalMutation.mutate({ ...animal, shareAnnounce: data })
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        p: 3,
        maxWidth: 500,
        backgroundColor: '#fafafa',
        borderRadius: 2,
        boxShadow: 1,
      }}
    >
      <Typography variant="h6" gutterBottom>
        {`Paramétrer la diffusion de l'annonce d'adoption`}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Votre annonce sera diffusée sur les sites sélectionnés uniquement si son statut est « adoptable » et que son
        profil est suffisamment complété.
      </Typography>

      <Tooltip title={disabledTooltip} arrow disableHoverListener={isAdoptable}>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <FormControl component="fieldset">
            <FormGroup sx={{ gap: 1 }}>
              <Controller
                name="petso"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    disabled={!isAdoptable}
                    control={
                      <Switch
                        checked={value}
                        onChange={(_, checked) => onChange(checked)}
                        color="primary"
                        disabled={!isAdoptable}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">Partager sur</Typography>
                        <img
                          src={'https://www.petso.fr/_next/static/media/logo.19b70203.svg'}
                          alt="Petso Logo"
                          style={{ height: 32, marginLeft: 4 }}
                        />
                        <Typography
                          variant="body1"
                          fontFamily="insaniburger"
                          fontSize={20}
                          color={theme.palette.primary.main}
                        >
                          Petso
                        </Typography>
                        <Tooltip title="Voir la page publique de l'animal" arrow>
                          <span>
                            <IconButton
                              component="a"
                              href={`https://petso.fr/annonces/${animal.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                              disabled={!isAdoptable || !value}
                              sx={{ ml: 0.5 }}
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    }
                  />
                )}
              />

              <Controller
                name="wamiz"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    disabled={!isAdoptable}
                    control={
                      <Switch
                        checked={value}
                        onChange={(_, checked) => onChange(checked)}
                        color="primary"
                        disabled={!isAdoptable}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">Partager sur</Typography>
                        <img
                          src="https://wamiz.com/build/images/newsletter/wamiz-logo.png"
                          alt="Wamiz Logo"
                          style={{ height: 24, marginLeft: 8 }}
                        />
                      </Box>
                    }
                  />
                )}
              />
            </FormGroup>
          </FormControl>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <LoadingButton
              type="submit"
              variant="outlined"
              color="primary"
              disabled={!isAdoptable || editAnimalMutation.isLoading}
              loading={editAnimalMutation.isLoading}
              startIcon={<Save />}
              sx={{ textTransform: 'none' }}
            >
              Mettre à jour
            </LoadingButton>
          </Box>
        </Box>
      </Tooltip>
    </Box>
  )
}
